package com.vizio.redwolf.pay.model

import com.vizio.redwolf.pay.api.SubscriptionService.Companion.ERROR_CONTENT_TYPE_NOT_SUPPORTED
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.ResponseHeaders
import com.vizio.redwolf.utils.TimeoutException
import com.vizio.redwolf.utils.model.PayFailureResponse
import io.ktor.client.call.NoTransformationFoundException
import io.ktor.client.call.body
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.request
import io.ktor.http.HttpStatusCode
import kotlin.js.JsExport

@JsExport
sealed class SubscriptionResult<out T : Any> {
    data class Success<out T : Any>(val headers: ResponseHeaders, val data: T) : SubscriptionResult<T>()
    class NotFound : SubscriptionResult<Nothing>()
    class Unauthorized : SubscriptionResult<Nothing>()
    data class Failed(val headers: ResponseHeaders, val response: PayFailureResponse) : SubscriptionResult<Nothing>()
    data class Error(val headers: ResponseHeaders, val exception: Throwable) : SubscriptionResult<Nothing>()
    data class Timeout(val headers: ResponseHeaders, val exception: Throwable) : SubscriptionResult<Nothing>()

    fun <R> fold (
        onSuccess: (Success: Success<T>) -> R,
        onNotFound: (NotFound: NotFound) -> R,
        onUnauthorized: (Unauthorized: Unauthorized) -> R,
        onFailed: (Failed: Failed) -> R,
        onError: (Error: Error) -> R,
        onTimeout: (Timeout: Timeout) -> R
    ) : R {
        return when (this) {
            is Success -> onSuccess(Success(this.headers, this.data))
            is NotFound -> onNotFound(NotFound())
            is Unauthorized -> onUnauthorized(Unauthorized())
            is Failed -> onFailed(Failed(this.headers, this.response))
            is Error -> onError(Error(this.headers, this.exception))
            is Timeout -> onTimeout(Timeout(this.headers, this.exception))
        }
    }
}

suspend inline fun <T : HttpResponse, reified R : Any> handleSubscriptionHttpResult(httpResult: HttpResult<T>): SubscriptionResult<R> {
    return when (httpResult) {
        is HttpResult.Success -> {
            val httpResponse: HttpResponse = httpResult.data
            val headers = ResponseHeaders.Valid(httpResponse.status.value)
            val requestUrl: String = httpResponse.request.url.toString()
            when (httpResponse.status) {
                HttpStatusCode.OK -> {
                    try {
                        SubscriptionResult.Success(headers, httpResponse.body())
                    } catch (ex: Exception) {
                        ex.printStackTrace()
                        SubscriptionResult.Error(headers, ex)
                    }
                }
                HttpStatusCode.NotFound -> SubscriptionResult.NotFound()
                HttpStatusCode.Unauthorized -> {
                    SubscriptionResult.Unauthorized()
                }
                else -> {
                    try {
                        val payFailureResponse = httpResponse.body<PayFailureResponse>()
                        SubscriptionResult.Failed(headers, payFailureResponse)
                    } catch (ex: NoTransformationFoundException) {
                        ex.printStackTrace()
                        val exception = Exception("$ERROR_CONTENT_TYPE_NOT_SUPPORTED:$requestUrl")
                        SubscriptionResult.Error(headers, exception)
                    } catch (ex: Exception) {
                        ex.printStackTrace()
                        SubscriptionResult.Error(headers, ex)
                    }
                }
            }
        }
        is HttpResult.Error -> {
            val headers = ResponseHeaders.Invalid
            if (httpResult.exception is TimeoutException)
                SubscriptionResult.Timeout(headers, httpResult.exception)
            else
                SubscriptionResult.Error(headers, httpResult.exception)
        }
    }
}
